import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SlotImage from '@/components/base/SlotImage';
import { useSiteSettings } from '@/hooks/useSiteSettings';

export default function WelcomeSection() {
  const { data: settings } = useSiteSettings();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 md:py-28 bg-background-50 overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left — Image */}
          <div className="reveal-left relative">
            <div className="img-zoom rounded-2xl overflow-hidden h-[400px] md:h-[520px]">
              <SlotImage
                slot="welcome_image"
                fallback="https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/603ca32a-3de9-4ccf-97a7-9ef2481f8546_compressed_WhatsApp-Image-2026-09-12-at-15.29.03-5.webp"
                alt="TGMCI church community welcome gathering"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-5 shadow-lg border border-background-200">
              <div className="text-3xl font-heading font-bold text-primary-600">10+</div>
              <div className="text-xs font-label text-foreground-500 mt-1 uppercase tracking-wide">Years of Ministry</div>
            </div>
            <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-accent-100 opacity-60 -z-10" />
          </div>

          {/* Right — Content */}
          <div className="reveal">
            <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">
              Our Story &amp; Mission
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight mb-6">
              {settings?.welcome_title || 'Empowering Communities Through Faith and Service'}
            </h2>
            <div className="w-12 h-1 bg-accent-500 mb-6" />
            <p className="text-foreground-600 text-base leading-relaxed mb-5">
              {settings?.welcome_text || 'At The Great Multitude Church International, we believe in the transformative power of faith. Our mission is to nurture the spirit of our youth, equipping them to lead with purpose.'}
            </p>
            <p className="text-foreground-600 text-base leading-relaxed mb-8">
              We are committed to serving our community and creating a supportive environment for everyone — fostering hope, compassion, and unity across Edea, Cameroon, Accra, Ghana, and beyond.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {[
                { icon: 'ri-flashlight-line', label: 'Our Mission', text: settings?.mission || 'To empower youth and serve through faith-driven initiatives.' },
                { icon: 'ri-eye-line', label: 'Our Vision', text: settings?.vision || 'To inspire young leaders to make a difference in their communities.' },
              ].map((item) => (
                <div key={item.label} className="flex gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 shrink-0">
                    <i className={`${item.icon} text-lg`} aria-hidden="true" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-foreground-800 text-sm">{item.label}</div>
                    <div className="text-foreground-500 text-xs mt-0.5 leading-relaxed">{item.text}</div>
                  </div>
                </div>
              ))}
            </div>

            <Link to="/about" className="btn-primary">
              <i className="ri-book-open-line" aria-hidden="true" />
              Read Our Story
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}